import type { IOfficeNew } from "../interfaces/models/IOffice";
import Axios from "./axios";

import { parseCalendarDate } from "./dates";

export const formatDateToString = (date: Date | undefined): string => {
  if (!date) {
    return "";
  }
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

/** Calendar date from a unix-seconds event timestamp, in UTC (no local-day offset). */
export const formatUnixTimestampToUtcDateString = (
  timestamp: number | undefined | null,
): string => {
  if (timestamp == null || Number.isNaN(Number(timestamp))) {
    return "";
  }
  const date = new Date(Number(timestamp) * 1000);
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

/** Legacy financial.asp: first 3 letters of month name + "." (e.g. "Mar.") */
export const formatBillingPeriod = (
  billingStartDate: string | Date | undefined | null,
): string => {
  const date = parseCalendarDate(billingStartDate);
  if (!date) {
    return "";
  }
  const month = date.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  return `${month.slice(0, 3)}.`;
};

export const formatTimeSpanToString = (value: string | number): string => {
  if (!value) {
    return "";
  }
  const numValue = Number(value) * 1000;
  return new Date(numValue).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const getNameById = (field: any, data: any) => {
  if (!field) {
    return [];
  }
  if (!data || !Array.isArray(data)) {
    return [];
  }
  return data.filter((d: any) => field.includes(d.id)).map((r: any) => r.name);
};

export const isUUID = (value: string): boolean => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

const axios = new Axios();
export const updateData = async (
  endpoint: string,
  fieldName: string,
  beforeValue: any,
  fieldValue: any,
) => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        [fieldName]: { value: beforeValue, comparisonType: "EQUAL" },
      }),
    );
    axios.Get(`/${endpoint}?${params.toString()}`).then((response) => {
      response.data.map((data: any) => {
        const updatedData = {
          ...data,
          [fieldName]: fieldValue,
        };
        console.log(updatedData);
        axios.Put(endpoint, updatedData);
      });
    });
  } catch (error) {
    console.error(`Error updating ${endpoint}:`, error);
  }
};

export const getCurrentTimestamp = () => {
  return Math.floor(new Date().getTime() / 1000);
};

/** Returns "start - end" in Unix seconds for "Last 6 months" (from 6 months ago to now). */
export const getLast6MonthsEpochRange = (): string => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  const startSeconds = Math.floor(start.getTime() / 1000);
  const endSeconds = Math.floor(end.getTime() / 1000);
  return `${startSeconds} - ${endSeconds}`;
};

/** Returns "start - end" in Unix seconds for the full year 2026 (Jan 1 00:00 to Dec 31 23:59:59). */
export const get2026EpochRange = (): string => {
  const start = new Date(2026, 0, 1, 0, 0, 0, 0);
  const end = new Date(2026, 11, 31, 23, 59, 59, 999);
  return `${Math.floor(start.getTime() / 1000)} - ${Math.floor(end.getTime() / 1000)}`;
};

/** Converts a date range string (e.g. "MM/DD/YYYY - MM/DD/YYYY") to Unix seconds "start - end" for BETWEEN_TS. End is end-of-day so the range is inclusive. */
export const convertDateRangeToEpoch = (dateRange: string): string => {
  if (!dateRange || typeof dateRange !== "string") {
    return "";
  }
  const parts = dateRange
    .split(" - ")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  const startDateString = parts[0];
  const endDateString = parts.length > 1 ? parts[1] : parts[0];
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "";
  }
  const startSeconds = Math.floor(startDate.getTime() / 1000);
  endDate.setHours(23, 59, 59, 999);
  const endSeconds = Math.floor(endDate.getTime() / 1000);
  return `${startSeconds} - ${endSeconds}`;
};

export const generateRandomPassword = () => {
  const length = 8;
  const charset =
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const specialChars = "!@#$%^&*()_+[]{}|;:,.<>?";

  let password = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(
    Math.floor(Math.random() * 26),
  );
  password += "0123456789".charAt(Math.floor(Math.random() * 10));
  password += specialChars.charAt(
    Math.floor(Math.random() * specialChars.length),
  );

  for (let i = 3; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }

  return password
    .split("")
    .sort(() => 0.5 - Math.random())
    .join("");
};

const stateLevel = ["United States of America", "Canada", "USA"];
export const formatLocationText = (office: IOfficeNew): string => {
  let country = office?.countryName.trim() || "";
  if (country === "United States of America") {
    country = "USA";
  }
  if (stateLevel.includes(country)) {
    const state = office?.stateName?.trim() || "";
    return `${state}, ${country}`;
  }

  const city = office?.cityName.trim() || "";
  if (!city && !country) {
    return "";
  }
  if (!city) {
    return country;
  }

  return `${city}, ${country}`;
};

export const formatTextForUrl = (text: string): string => {
  return (text ?? "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .replace(/[áàäâ]/g, "a")
    .replace(/[éèëê]/g, "e")
    .replace(/[íìïî]/g, "i")
    .replace(/[óòöô]/g, "o")
    .replace(/[úùüû]/g, "u")
    .replace(/[ñ]/g, "n")
    .replace(/[ç]/g, "c");
};

export function formatOfficeText(office: IOfficeNew): string {
  let country =
    office?.countryName === "United States of America"
      ? "USA"
      : office?.countryName;
  return `${country ?? ""} - ${office?.stateName ? office?.stateName + "," : ""} ${office?.cityName ?? ""}`;
}
